import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AlumniLayout from "@/components/AlumniLayout";
import SortAlphaBar, { sortFaculty, letterOfFaculty } from "@/components/SortAlphaBar";
import { useUiState } from "@/lib/uiState";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  GraduationCap, Star, CalendarRange, ArrowRight, Globe, Linkedin, BookMarked,
  Flower2, Search, UserSquare2, Loader2, X,
} from "lucide-react";

const STATUS_STYLES = {
  current: "bg-emerald-100 text-emerald-800", retired: "bg-sky-100 text-sky-800",
  emeritus: "bg-amber-100 text-amber-800", deceased: "bg-slate-200 text-slate-700",
};
const STATUS_LABELS = { current: "Current", retired: "Retired", emeritus: "Emeritus", deceased: "Deceased" };
const tenure = (s) => {
  if (s.year_start == null && s.year_end == null) return "";
  const a = s.year_start ?? "?";
  const b = s.year_end == null ? "present" : s.year_end;
  return `${a}–${b}`;
};
const Initials = ({ name, cls }) => <span className={cls}>{(name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase()}</span>;

function LinkChips({ s }) {
  return (
    <>
      {s.wikipedia && <a href={s.wikipedia} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border hover:bg-muted"><BookMarked className="h-3 w-3" /> Wikipedia</a>}
      {s.linkedin && <a href={s.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border hover:bg-muted"><Linkedin className="h-3 w-3" /> LinkedIn</a>}
      {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-border hover:bg-muted"><Globe className="h-3 w-3" /> Website</a>}
    </>
  );
}

function DetailModal({ s, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="alumni-faculty-detail">
        <DialogHeader className="sr-only"><DialogTitle>{s.name}</DialogTitle><DialogDescription>Faculty detail</DialogDescription></DialogHeader>
        <div className="flex items-start gap-4">
          <div className="h-20 w-20 rounded-full bg-accent/15 flex items-center justify-center overflow-hidden border border-border shrink-0">
            {s.photo ? <img src={s.photo} alt={s.name} className="h-full w-full object-cover" /> : <Initials name={s.name} cls="text-2xl font-semibold text-primary" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-serif text-2xl leading-tight">{s.name}</h2>
              <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${STATUS_STYLES[s.status] || ""}`}>{STATUS_LABELS[s.status] || s.status}</span>
            </div>
            {s.title && <div className="text-sm text-foreground/90 mt-0.5">{s.title}</div>}
            {s.department && <div className="text-sm text-muted-foreground">{s.department}</div>}
            {tenure(s) && <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1"><CalendarRange className="h-3.5 w-3.5" /> {tenure(s)}</div>}
          </div>
        </div>
        {s.bio && <p className="text-sm text-foreground/85 mt-4 whitespace-pre-line leading-relaxed">{s.bio}</p>}
        {(s.wikipedia || s.linkedin || s.website || s.alumnus_id) && (
          <div className="flex flex-wrap gap-2 mt-4">
            {s.alumnus_id && <a href={`/alumni/${s.alumnus_id}`} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-accent/20 text-primary hover:bg-accent/30"><UserSquare2 className="h-4 w-4" /> Alumni page</a>}
            <LinkChips s={s} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AlumniFaculty() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [viewing, setViewing] = useState(null);
  const [sortBy, setSortBy] = useUiState("ud_afaculty_sort", "first");
  const navigate = useNavigate();

  useEffect(() => { api.get("/experience/faculty").then((r) => setData(r.data)).catch(() => setData({ spotlight: null, active: [], in_memoriam: [] })); }, []);

  const active = data?.active || [];
  const memoriam = data?.in_memoriam || [];
  const yr = yearFilter === "" ? null : parseInt(yearFilter, 10);
  const overlapsYear = (m, y) => {
    const lo = m.year_start == null ? -Infinity : m.year_start;
    const hi = m.year_end == null ? Infinity : m.year_end;
    return lo <= y && hi >= y;
  };
  const filtered = useMemo(() => {
    // When a year is chosen, show EVERYONE (incl. deceased) who was here that year.
    let base = yr != null ? [...active, ...memoriam].filter((m) => overlapsYear(m, yr)) : active;
    const t = q.trim().toLowerCase();
    if (t) base = base.filter((s) => [s.name, s.title, s.department].filter(Boolean).join(" ").toLowerCase().includes(t));
    return sortFaculty(base, sortBy);
  }, [active, memoriam, q, sortBy, yr]);
  const available = useMemo(() => new Set(filtered.map((s) => letterOfFaculty(s.name, sortBy)).filter(Boolean)), [filtered, sortBy]);
  const jump = (L) => { const first = filtered.find((s) => letterOfFaculty(s.name, sortBy) === L); if (first) document.getElementById(`afac-item-${first.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const s = data?.spotlight;

  return (
    <AlumniLayout wide>
      <div className="mb-6">
        <div className="flex items-center gap-2 text-primary"><GraduationCap className="h-6 w-6" /><h1 className="font-serif text-3xl">Faculty &amp; Staff</h1></div>
        <p className="text-sm text-muted-foreground mt-1">The professors and staff who shaped our community — past and present.</p>
      </div>

      {data === null ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-16 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          {s && (
            <section className="mb-9" data-testid="faculty-spotlight">
              <h2 className="font-serif text-2xl tracking-tight mb-4 flex items-center gap-2.5"><span className="inline-block h-6 w-1 rounded-full bg-accent" />Faculty Spotlight</h2>
              <div className="relative overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-accent to-accent/40" />
                <div className="flex flex-col sm:flex-row gap-5 p-5 sm:p-6">
                  <div className="h-28 w-28 rounded-full bg-accent/15 flex items-center justify-center overflow-hidden border-2 border-accent/30 shrink-0 mx-auto sm:mx-0">
                    {s.photo ? <img src={s.photo} alt={s.name} className="h-full w-full object-cover" /> : <Initials name={s.name} cls="text-3xl font-semibold text-primary" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-accent"><Star className="h-3.5 w-3.5 fill-accent" /> A professor you may remember</div>
                    <h3 className="font-serif text-2xl tracking-tight mt-1" data-testid="spotlight-name">{s.name}</h3>
                    <div className="text-sm text-muted-foreground">{[s.title, s.department].filter(Boolean).join(" · ")}</div>
                    {tenure(s) && <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1"><CalendarRange className="h-3.5 w-3.5" /> {tenure(s)}</div>}
                    {s.bio && <p className="text-sm text-foreground/85 mt-3 leading-relaxed line-clamp-4">{s.bio}</p>}
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      <button data-testid="spotlight-reunion-cta" onClick={() => navigate("/experience/events")} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">Reconnect at a reunion <ArrowRight className="h-4 w-4" /></button>
                      <LinkChips s={s} />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="mb-9" data-testid="faculty-directory">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2.5"><span className="inline-block h-6 w-1 rounded-full bg-primary/60" />{yr != null ? `On Campus in ${yr}` : "Faculty & Staff Directory"}</h2>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input data-testid="faculty-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, title, department…" className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-background text-sm" />
                </div>
                <div className="relative shrink-0">
                  <input data-testid="faculty-year-filter" type="number" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} placeholder="Year…" className="w-24 px-3 py-2 rounded-md border border-border bg-background text-sm" />
                  {yr != null && <button data-testid="faculty-year-clear" onClick={() => setYearFilter("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive" title="Clear year"><X className="h-3.5 w-3.5" /></button>}
                </div>
              </div>
            </div>
            {yr != null && <p className="text-xs text-muted-foreground -mt-2 mb-3">Showing everyone who was on campus in {yr} (including those no longer with us).</p>}
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="faculty-directory-empty">No faculty match your search.</p>
            ) : (
              <>
              <SortAlphaBar sortBy={sortBy} onSort={setSortBy} available={available} onJump={jump} idPrefix="afac" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map((m) => (
                  <button key={m.id} id={`afac-item-${m.id}`} data-testid={`faculty-member-${m.id}`} onClick={() => setViewing(m)} className="text-left bg-card border border-border rounded-lg p-4 hover:shadow-md hover:border-primary/40 transition-all scroll-mt-24">
                    <div className="flex items-start gap-3">
                      <div className="h-12 w-12 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden border border-border shrink-0">
                        {m.photo ? <img src={m.photo} alt={m.name} className="h-full w-full object-cover" loading="lazy" /> : <Initials name={m.name} cls="text-sm font-semibold text-primary" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-foreground truncate">{m.name}</div>
                        {m.title && <div className="text-xs text-muted-foreground truncate">{m.title}</div>}
                        {m.department && <div className="text-[11px] text-muted-foreground truncate">{m.department}</div>}
                      </div>
                      <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded shrink-0 ${STATUS_STYLES[m.status] || ""}`}>{STATUS_LABELS[m.status] || m.status}</span>
                    </div>
                    {tenure(m) && <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground"><CalendarRange className="h-3.5 w-3.5" /> {tenure(m)}</div>}
                  </button>
                ))}
              </div>
              </>
            )}
          </section>

          {memoriam.length > 0 && yr == null && (
            <section data-testid="faculty-in-memoriam">
              <h2 className="font-serif text-2xl tracking-tight mb-1 flex items-center gap-2.5"><span className="inline-block h-6 w-1 rounded-full bg-slate-400" />In Memoriam</h2>
              <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5"><Flower2 className="h-3.5 w-3.5" /> Honoring the faculty and staff who shaped our community.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {memoriam.map((m) => (
                  <button key={m.id} data-testid={`memoriam-${m.id}`} onClick={() => setViewing(m)} className="text-left flex items-center gap-3 bg-muted/40 border border-border/60 rounded-lg p-3 hover:border-border">
                    <div className="h-12 w-12 rounded-full bg-background flex items-center justify-center overflow-hidden border border-border shrink-0 grayscale">
                      {m.photo ? <img src={m.photo} alt={m.name} className="h-full w-full object-cover" loading="lazy" /> : <Initials name={m.name} cls="text-sm font-semibold text-muted-foreground" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-serif text-base leading-tight truncate">{m.name}</div>
                      {m.title && <div className="text-xs text-muted-foreground truncate">{m.title}</div>}
                      {tenure(m) && <div className="text-[11px] text-muted-foreground/80">{tenure(m)}</div>}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {viewing && <DetailModal s={viewing} onClose={() => setViewing(null)} />}
    </AlumniLayout>
  );
}
